import { useEffect } from 'react';
import { MessageCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useChatWidgetStore } from '@/stores/chatWidgetStore';
import { useChatSession } from '@/hooks/useChatSession';
import { useLocales } from '@/locales';
import MessageList from './MessageList';
import Composer from './Composer';
import ContactForm from './ContactForm';
import HandoffBanner from './HandoffBanner';

export default function ChatWidget() {
  const { translate } = useLocales();
  const isOpen = useChatWidgetStore(state => state.isOpen);
  const messages = useChatWidgetStore(state => state.messages);
  const handoffActive = useChatWidgetStore(state => state.handoffActive);
  const toggle = useChatWidgetStore(state => state.toggle);
  const close = useChatWidgetStore(state => state.close);

  const { sendMessage, isSending } = useChatSession();

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = e => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, close]);

  return (
    <>
      {!isOpen && (
        <Button
          type="button"
          size="icon"
          onClick={toggle}
          aria-label={translate('chat_launcher_aria')}
          className="fixed bottom-5 right-5 z-50 h-14 w-14 rounded-full shadow-lg bg-[#004263] hover:bg-[#003150] text-white [&_svg]:size-6"
        >
          <MessageCircle />
        </Button>
      )}

      {isOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={translate('chat_title')}
          className="fixed z-50 bg-white shadow-2xl flex flex-col overflow-hidden bottom-0 right-0 w-full h-[100dvh] md:bottom-5 md:right-5 md:h-[600px] md:max-h-[80vh] md:w-96 md:rounded-2xl border border-gray-200"
        >
          <div className="flex items-center justify-between bg-[#004263] px-4 py-3 text-white">
            <div className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5" />
              <h4 className="font-semibold text-sm">
                {translate('chat_title')}
              </h4>
            </div>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={close}
              className="h-8 w-8 text-white hover:bg-white/10 hover:text-white"
              aria-label={translate('chat_close')}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {handoffActive && <HandoffBanner />}

          <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
            <MessageList messages={messages} isSending={isSending} />
          </div>

          <ContactForm />
          <Composer
            onSend={sendMessage}
            disabled={isSending}
            handoffActive={handoffActive}
          />
        </div>
      )}
    </>
  );
}
